import { ajax } from '@tencent/qd-lib';
import formDataUrlList from '@/config/formDataUrl';
import loginPanel from '@/utils/loginCommon';
import { getCurrentLocale } from '@/utils/locale-reader';
import Gateway from '@/utils/Gateway';
// 会存在没有code | r的情况，故创建白名单，在白名单里的接口直接返回数据
import whiteListUrl from '@/config/whiteListUrl';
// 针对页面上r非0或者code非0时，业务上有处理，需要返回res的接口
import businessErrorWhiteList from '@/config/businessErrorWhiteList';

/* ============================================================================
 * 接口数据国际化字典 —— ajax 层接管
 *
 * 设计（与 qidian-common 组件库 i18n 模块对齐）：
 *   - 翻译字典接口 `POST /v1/kf/i18n/GetI18nTextMap` 的加载与 i18n.merge 完全在 ajax 层完成；
 *   - 请求体携带 `{ get_type: 'all' }`，与 internal/qidian-common 等仓库保持一致；
 *   - 业务每次发起接口请求时，请求拦截器会"先等翻译字典就绪、再放行"；
 *   - 并发请求合并为一次（single-flight）；失败不缓存 Promise，下次可重试；
 *   - 静默期内（FETCH_THROTTLE_MS）的重复触发直接复用上次结果，避免 mock/弱网下打爆；
 *   - sessionStorage 缓存：reload 后同步可读，消除"先中文后英文"的闪烁；
 *   - zh-CN 等非英文环境完全不阻塞、不发请求；
 *   - 翻译接口自身被排除在等待逻辑外，避免死锁；
 *   - 失败不阻塞业务：字典加载失败也会放行原始请求（业务文案回退中文原文）。
 *
 * 业务无须感知 / 无须主动调 $loadTran：
 *   只要 main.ts 里 `setI18nForTran(i18n)` 注入了 i18n 实例，
 *   所有通过本 request 实例发出的接口都会自动等待字典就绪后再发。
 * ============================================================================ */

/** 翻译接口路径（不参与等待逻辑） */
const TRAN_API_URL = '/v1/kf/i18n/GetI18nTextMap';

/** sessionStorage 缓存 key（与 qidian-common 保持一致，便于跨仓库联调对账） */
const I18N_CACHE_KEY = 'qidian_i18n_en_textmap';

/** 静默期：N 毫秒内不重复发起远程请求 */
const FETCH_THROTTLE_MS = 5000;

/** 业务侧通过 setI18nForTran 注入的 vue-i18n 实例 */
let i18nForTran: any = null;

/** 正在进行中的请求 Promise（并发合并）；完成后立即置 null，失败可重试 */
let inflightPromise: Promise<void> | null = null;

/** 上次成功完成加载的时间戳，用于静默期判断 */
let lastFetchAt = 0;

/** 上次完成加载时的 locale，用于切语言 invalidate */
let dictLoadedLocale: string | null = null;

/**
 * 注入 vue-i18n 实例。在业务入口尽早调用一次。
 * 未注入时拦截器仍能正常放行（仅"不进行 i18n.merge"），不影响请求。
 *
 * 注入后会立即把 sessionStorage 缓存的字典 merge 进当前 i18n，
 * 这样 reload 后业务首屏就能拿到上次的英文文案，不会出现中文闪烁。
 */
export function setI18nForTran(i18n: any): void {
    if (!i18n) return;
    i18nForTran = i18n;
    // 启动同步 merge：消除 reload 闪烁
    const cached = loadCachedTextMap();
    if (cached) mergeDictIntoI18n(cached);
}

/**
 * 从 sessionStorage 同步读取缓存的翻译 map。
 * 仅在 en-US 环境下读取；失败/解析异常时返回 null。
 */
function loadCachedTextMap(): { [k: string]: string } | null {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    if (getCurrentLocale() !== 'en-US') return null;
    try {
        const raw = window.sessionStorage.getItem(I18N_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
        return null;
    }
}

/**
 * 将翻译 map 写入 sessionStorage 缓存（quota 异常等忽略）。
 */
function saveCachedTextMap(textMap: { [k: string]: string }): void {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    try {
        window.sessionStorage.setItem(I18N_CACHE_KEY, JSON.stringify(textMap));
    } catch (e) {
        // ignore quota
    }
}

/**
 * 把后端字典 merge 进 vue-i18n.messages（仅 en-US）。
 */
function mergeDictIntoI18n(dict: { [k: string]: string }): void {
    if (!i18nForTran || !dict || typeof dict !== 'object') return;
    const locale = getCurrentLocale();
    if (locale !== 'en-US') return;

    if (typeof i18nForTran.mergeLocaleMessage === 'function') {
        i18nForTran.mergeLocaleMessage(locale, dict);
        return;
    }
    const existing = (i18nForTran.messages && i18nForTran.messages[locale]) || {};
    if (typeof i18nForTran.setLocaleMessage === 'function') {
        i18nForTran.setLocaleMessage(locale, Object.assign({}, existing, dict));
    } else if (i18nForTran.messages) {
        i18nForTran.messages[locale] = Object.assign({}, existing, dict);
    }
}

/**
 * 拉取接口数据国际化字典（en-US 时使用）。
 *
 * 真实接口：POST /v1/kf/i18n/GetI18nTextMap
 *   - 请求体：{ get_type: 'all' }（与 qidian-common / internal 仓库对齐；后端预留分桶能力）
 *   - 响应：{ code: 0, data: { texts: { '中文': '展示文案', ... } } }
 *
 * 失败时返回空字典，调用方继续按原文渲染。
 */
export function fetchAccountTranDict(): Promise<{ [k: string]: string }> {
    return Gateway.post(TRAN_API_URL, { get_type: 'all' })
        .then((data: any) => (data && data.texts) || {})
        .catch(() => ({}));
}

/**
 * 确保翻译字典已就绪（加载 + merge 进 i18n）。
 * 多次调用幂等；切语言后自动重新加载。
 *
 * - zh-CN / 其他：直接 resolve（不发请求）；
 * - en-US：
 *     · 静默期内 → 直接 resolve（复用上次结果）；
 *     · 已有 inflight → 复用同一 Promise（single-flight）；
 *     · 否则发起请求，成功后 merge 进 i18n + 写 sessionStorage；失败不缓存 Promise，下次可重试。
 *
 * @returns Promise<void> 始终 resolve（失败也不 reject，不阻塞业务）
 */
export function ensureTranDictReady(): Promise<void> {
    const currentLocale = getCurrentLocale();

    // 切语言：重置内存状态（sessionStorage 由 saveCachedTextMap 自然覆盖）
    if (dictLoadedLocale && dictLoadedLocale !== currentLocale) {
        inflightPromise = null;
        lastFetchAt = 0;
        dictLoadedLocale = null;
    }

    // 非英文：不发请求
    if (currentLocale !== 'en-US') {
        dictLoadedLocale = currentLocale;
        return Promise.resolve();
    }

    // 静默期内：复用上次结果
    if (lastFetchAt && Date.now() - lastFetchAt < FETCH_THROTTLE_MS) {
        return Promise.resolve();
    }

    // single-flight：并发合并
    if (inflightPromise) return inflightPromise;

    inflightPromise = fetchAccountTranDict()
        .then((dict) => {
            mergeDictIntoI18n(dict);
            if (dict && Object.keys(dict).length > 0) {
                // 成功且非空：与已有缓存合并写回（保留旧条目）
                const existing = loadCachedTextMap() || {};
                saveCachedTextMap(Object.assign({}, existing, dict));
            }
            lastFetchAt = Date.now();
            dictLoadedLocale = currentLocale;
        })
        .catch((err) => {
            // 失败不更新 lastFetchAt，下次仍可立刻重试；不影响业务请求
            // eslint-disable-next-line no-console
            if (typeof console !== 'undefined') console.warn('[ajax] tran dict load failed:', err);
        })
        .then(() => {
            // 无论成败，inflight 都释放，避免单例 Promise 永久卡死
            inflightPromise = null;
        });

    return inflightPromise;
}

/** 强制重新加载字典（开发期 mock 数据热更新使用） */
export function reloadTranDict(): Promise<void> {
    inflightPromise = null;
    lastFetchAt = 0;
    dictLoadedLocale = null;
    return ensureTranDictReady();
}

// 开发期暴露到 window，便于在控制台手动刷新字典
if (typeof window !== 'undefined') {
    // @ts-ignore
    if (!window.__reloadTran__) {
        // @ts-ignore
        window.__reloadTran__ = reloadTranDict;
    }
}

/**
 * 判断 url 是否是翻译接口本身（避免拦截器等待自己导致死锁）。
 * 业务里 originUrl 形如 '/v1/kf/i18n/GetI18nTextMap'。
 */
function isTranSelfRequest(config: any): boolean {
    const url = (config && (config.originUrl || config.url)) || '';
    return typeof url === 'string' && url.indexOf(TRAN_API_URL) !== -1;
}

const request = ajax.create({
    interceptors: {
        // 拦截器返回 Promise<config>，axios 会等 resolve 后再发请求
        requestInterceptors: (config: any) => {
            config.nopermission = loginPanel;
            config.headers = config.headers || {};
            // 强制对齐 Accept-Language 与当前 qidianLocale。
            // 即使调用方已显式传入（与当前语言不符），也以当前语言为准，
            // 避免运行时切语言后请求头仍是旧语言。
            const currentLocale = getCurrentLocale();
            if (config.headers['Accept-Language'] !== currentLocale) {
                config.headers['Accept-Language'] = currentLocale;
            }
            if (formDataUrlList.includes(config.originUrl)) config.headers['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8';

            // ✨ 核心：业务请求发出前，先等翻译字典就绪。
            // - 翻译接口本身不等（避免死锁）
            // - 非 en-US 立即 resolve，无延迟
            if (isTranSelfRequest(config)) {
                return config;
            }
            return ensureTranDictReady().then(() => config);
        },
        responseInterceptors: (res: any) => {
            if (res && res.data && res.data.r === 0) return res;
            // 这段代码会影响业务中未处理r非0的情况，比如r非0时在页面还会报成功
            // // fix 这里r参数有值 也要返回 不然业务无法捕获到错误
            // if (res && res.data && res.data.r) return res;
            if (res && res.data && res.data.code !== 0) {
                if (res.config.customFn) {
                    res.config.customFn(res.data);
                } else {
                    if (whiteListUrl.includes(res.config.originUrl) || businessErrorWhiteList.includes(res.config.originUrl)) return res;
                    // @ts-ignore
                    !res.config.noShowMsg && window.vhtml?.Message?.error(res.data.msg || res.data.message || '信息加载出错，请稍后刷新重试');
                }
                return false;
            }
            return res;
        },
    },
});

request.crossGet = request.get;
request.crossPost = request.post;
request.read = request.get;
request.write = request.post;
request.update = request.put;
request.remove = request.delete;

export default request;
